import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Sparkles, 
  Target, 
  TrendingUp,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Clock
} from 'lucide-react';

const AssessmentProgressIndicator = ({ 
  isProcessing, 
  currentStep, 
  totalSteps, 
  stepName,
  error,
  onRetry,
  estimatedTime = 30 // seconds
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showRetry, setShowRetry] = useState(false);
  
  // Track elapsed time
  useEffect(() => {
    if (!isProcessing) {
      setElapsedTime(0);
      setShowRetry(false);
      return;
    }
    
    const timer = setInterval(() => {
      setElapsedTime(prev => {
        const newTime = prev + 1;
        // Show retry after 20 seconds
        if (newTime >= 20 && !showRetry) {
          setShowRetry(true);
        }
        return newTime;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isProcessing, showRetry]);
  
  // Process status messages based on current step
  const getStatusMessage = () => {
    if (error) return 'An error occurred. Please try again.';
    
    const messages = [
      'Analyzing your responses...',
      'Identifying patterns and themes...',
      'Generating strategic insights...',
      'Crafting your unique positioning...',
      'Finalizing recommendations...'
    ];
    
    return stepName || messages[Math.min(currentStep, messages.length - 1)];
  };
  
  // Calculate progress percentage
  const progressPercentage = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0;
  
  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };
  
  // Get icon for current step
  const getStepIcon = () => {
    const icons = [Brain, Sparkles, Target, TrendingUp, CheckCircle];
    const Icon = icons[Math.min(currentStep, icons.length - 1)];
    return <Icon className="w-5 h-5" />;
  };
  
  if (!isProcessing && !error) return null;
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-stone-50/95 to-teal-50/95 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-8">
        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/50">
          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 text-white mb-4 animate-pulse">
              {error ? <AlertCircle className="w-8 h-8" /> : getStepIcon()}
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {error ? 'Processing Error' : 'Processing Your Assessment'}
            </h2>
            
            <p className="text-stone-600 font-medium">
              {getStatusMessage()}
            </p>
          </div>
          
          {/* Progress Bar */}
          {!error && (
            <>
              <div className="mb-6">
                <div className="flex justify-between text-sm text-stone-500 mb-2">
                  <span>Step {currentStep + 1} of {totalSteps}</span>
                  <span>{Math.round(progressPercentage)}%</span>
                </div>
                
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-700 ease-out"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
              
              {/* Step Details */}
              <div className="space-y-2 mb-6">
                {Array.from({ length: totalSteps }, (_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                      i < currentStep ? 'bg-emerald-500' :
                      i === currentStep ? 'bg-teal-500 scale-110' :
                      'bg-stone-200'
                    }`}>
                      {i < currentStep ? (
                        <CheckCircle className="w-4 h-4 text-white" />
                      ) : i === currentStep ? (
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      ) : (
                        <div className="w-2 h-2 bg-stone-400 rounded-full" />
                      )}
                    </div>
                    
                    <span className={`text-sm font-medium transition-colors duration-300 ${
                      i < currentStep ? 'text-stone-600' :
                      i === currentStep ? 'text-teal-700' :
                      'text-stone-400'
                    }`}>
                      {[
                        'Reading responses',
                        'Finding patterns',
                        'Building insights',
                        'Creating strategy',
                        'Preparing results'
                      ][i]}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Time and Retry */}
              <div className="flex items-center justify-between flex-col">
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <Clock className="w-4 h-4" />
                  <span>Elapsed: {formatTime(elapsedTime)}</span>
                </div>
                
                {showRetry && !error && (
                  <button
                    onClick={onRetry}
                    className=" items-left gap-2 px-4 py-3 text-sm font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <p><strong>Did you know:</strong></p>
                    <p>Some colleges call the school when finalizing decisions, especially for borderline applicants. And they don’t just talk to counselors — they might chat with the front office or reception. A genuinely kind reputation can literally tip the scale in your favor.</p> 
                  </button>
                )}
              </div>
            </>
          )}
          
          {/* Error State */}
          {error && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <p className="text-sm text-red-700">
                  {error.message || 'Something went wrong while processing your assessment.'}
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={onRetry}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
              </div>
            </div>
          )}
          
          {/* Helpful Tips */}
          {!error && elapsedTime > 10 && (
            <div className="mt-6 p-4 bg-teal-50/50 rounded-xl border border-teal-100">
              <p className="text-xs text-teal-700">
                <strong> ⏰ This Process May Take Up to 4 Minutes:</strong> Critical analysis of your responses creates personalized insights. This thorough process ensures high-quality recommendations tailored specifically to you.
              </p>
            </div>
          )}
        </div>
        
        {/* Bottom Note */}
        <p className="text-center text-sm text-stone-500 mt-4">
          Please don't close this window
        </p>
      </div>
    </div>
  );
};

export default AssessmentProgressIndicator;